import { createEntityAdapter, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import ActionState, { ActionStatus } from 'store/ActionState';
import { IDed } from 'gql/Request';
import { EosService, ListEosServiceOptions } from 'gql/EosService';

export type EosServiceState = {
  eosService?: EosService;
  eosServices: EntityState<EosService>;
  loadingState: ActionState;
  mutationState: ActionState;
  saveForm?: EosService;
  mutatedEosServiceId?: string;
};

export const eosServiceAdapter = createEntityAdapter<EosService>();

export const initialState: EosServiceState = {
  eosServices: eosServiceAdapter.getInitialState(),
  loadingState: { status: ActionStatus.IDLE },
  mutationState: { status: ActionStatus.IDLE },
};

const EosServiceSlice = createSlice({
  name: 'eosService',
  initialState,
  reducers: {
    // LIST
    listEosServices: (state, _: PayloadAction<ListEosServiceOptions | undefined>): EosServiceState => {
      return { ...state, loadingState: { status: ActionStatus.PROCESSING } };
    },
    listEosServicesSucceeded: (state, action: PayloadAction<EosService[]>): EosServiceState => {
      return {
        ...state,
        eosServices:
          action.payload.length === 0
            ? state.eosServices
            : eosServiceAdapter.upsertMany({ ...state.eosServices }, action.payload),
        loadingState: { status: ActionStatus.SUCCEEDED },
      };
    },
    listEosServicesFailed: (state, action: PayloadAction<Error>): EosServiceState => {
      return { ...state, loadingState: { status: ActionStatus.FAILED, error: action.payload } };
    },

    // GET
    getEosService: (state, _: PayloadAction<string>): EosServiceState => {
      return { ...state, eosService: undefined, loadingState: { status: ActionStatus.PROCESSING } };
    },
    getEosServiceSucceeded: (state, action: PayloadAction<EosService>): EosServiceState => {
      return {
        ...state,
        eosService: action.payload,
        loadingState: { status: ActionStatus.SUCCEEDED },
      };
    },
    getEosServiceFailed: (state, action: PayloadAction<Error>): EosServiceState => {
      return { ...state, loadingState: { status: ActionStatus.FAILED, error: action.payload } };
    },

    // CREATE
    createEosService: (state, action: PayloadAction<EosService>): EosServiceState => {
      return {
        ...state,
        saveForm: action.payload,
        mutationState: { status: ActionStatus.PROCESSING },
        mutatedEosServiceId: undefined,
      };
    },
    createEosServiceSucceeded: (state, action: PayloadAction<IDed>): EosServiceState => {
      return {
        ...state,
        saveForm: undefined,
        mutationState: { status: ActionStatus.SUCCEEDED },
        mutatedEosServiceId: action.payload.id,
      };
    },
    createEosServiceFailed: (state, action: PayloadAction<Error>): EosServiceState => {
      return {
        ...state,
        saveForm: undefined,
        mutationState: { status: ActionStatus.FAILED, error: action.payload },
      };
    },

    // UPDATE
    updateEosService: (state, action: PayloadAction<EosService>): EosServiceState => {
      return {
        ...state,
        saveForm: action.payload,
        mutationState: { status: ActionStatus.PROCESSING },
      };
    },
    updateEosServiceSucceeded: (state, action: PayloadAction<IDed>): EosServiceState => {
      return {
        ...state,
        saveForm: undefined,
        mutationState: { status: ActionStatus.SUCCEEDED },
        mutatedEosServiceId: action.payload.id,
      };
    },
    updateEosServiceFailed: (state, action: PayloadAction<Error>): EosServiceState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.FAILED, error: action.payload },
        saveForm: undefined,
      };
    },

    // DELETE
    deleteEosService: (state, _: PayloadAction<string>): EosServiceState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.PROCESSING },
      };
    },
    deleteEosServiceSucceeded: (state, action: PayloadAction<IDed>): EosServiceState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.SUCCEEDED },
        mutatedEosServiceId: action.payload.id,
      };
    },
    deleteEosServiceFailed: (state, action: PayloadAction<Error>): EosServiceState => {
      return {
        ...state,
        mutationState: { status: ActionStatus.FAILED, error: action.payload },
        saveForm: undefined,
      };
    },
    // Clear
    clearState: () => {
      return initialState;
    },
  },
});

export default EosServiceSlice;
